/**
 * =============================================================================
 * IDS CRM Web - Quote / Contract Builder (sales rework Phase 6)
 * =============================================================================
 * Documents are generated FROM an Opportunity (group): only line items that
 * are approved or need no approval are includable (pending/rejected show
 * disabled with their status — they stay reviewable on the Opportunity).
 * Wording is editable via text blocks; prices come from the line items'
 * schedule snapshots and are NOT editable here.
 *
 * An accepted order also offers "Order acknowledgement" (Gareth 2026-10-19):
 * one wording block, and a "Covered by the repair & replace policy" tick:
 * ticked, the prices still show but the balance due is zero.
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BackToList from '../components/BackToList';
import { formatMoney } from '../utils/money';
import './QuoteDetail.css';
import './CommercialLayout.css';

// Mirrors the server's DEFAULT_TEXT_BLOCKS (services/quotePdf.js).
const DEFAULT_TEXT_BLOCKS = {
  intro: 'Please find below our proposal for {farm}. We would be delighted to discuss any aspect of the system or the installation with you.',
  site_survey_requirements: '',
  installation_wording: 'The one-off installation fee below (exc. VAT) is payable on receipt of invoice, issued two weeks prior to the installation date.',
  monthly_wording: 'The contract period is 60 months, after which there is a rolling 3-month contract.'
};

// Mirrors the server's ACK_TEXT_BLOCKS.
const ACK_TEXT_BLOCKS = {
  intro: 'Thank you for your order. We confirm that IDS has received and accepted the order below for {farm}.'
};

export const DOC_TYPE_NAMES = { contract: 'contract', quote: 'quote', acknowledgement: 'order acknowledgement' };

const TEXT_BLOCK_LABELS = {
  intro: 'Introduction',
  site_survey_requirements: 'Site Survey Requirements',
  installation_wording: 'Installation cost wording',
  monthly_wording: 'Monthly rental wording'
};

function lineEligibility(item) {
  if (item.line_type !== 'product') return { eligible: true, label: null };
  if (item.cancelled_at) return { eligible: false, label: 'cancelled' };
  if (item.lost_at) return { eligible: false, label: 'lost' };
  if (item.approval_status === 'pending') return { eligible: false, label: 'awaiting approval' };
  if (item.approval_status === 'rejected') return { eligible: false, label: 'rejected — action required' };
  return { eligible: true, label: null };
}

function QuoteBuilder() {
  const { id } = useParams();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();
  const { authFetch, user } = useAuth();
  // Operations coordinators produce order acknowledgements only (Gareth 2026-10-19).
  const ackOnly = user?.role === 'operations_coordinator';
  const navigate = useNavigate();

  const [groupId, setGroupId] = useState(searchParams.get('group_id') || '');
  const [group, setGroup] = useState(null);
  const [groupChoices, setGroupChoices] = useState(null); // customer path: pick a group
  const [customerName, setCustomerName] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [docType, setDocType] = useState('contract');
  const [repairReplace, setRepairReplace] = useState(false);
  const isAck = docType === 'acknowledgement';
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [textBlocks, setTextBlocks] = useState({ ...DEFAULT_TEXT_BLOCKS });
  const [included, setIncluded] = useState({}); // opportunity_id -> bool

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadGroup = useCallback(async (targetGroupId) => {
    const res = await authFetch(`/api/v1/opportunity-groups/${targetGroupId}`);
    if (!res.ok) throw new Error('Opportunity not found');
    const data = await res.json();
    setGroup(data.data);
    setCustomerName(data.data.customer_name);
    // An accepted order is acknowledged, not contracted (Gareth 2026-10-19).
    if (data.data.is_order && data.data.order_accepted_at) {
      setDocType('acknowledgement');
      setTextBlocks({ ...ACK_TEXT_BLOCKS });
    }
    setCurrency(data.data.line_items?.[0]?.currency || 'GBP');
    const defaults = {};
    for (const item of data.data.line_items || []) {
      defaults[item.opportunity_id] = lineEligibility(item).eligible;
    }
    setIncluded(defaults);
  }, [authFetch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (isEdit) {
        const q = await (await authFetch(`/api/v1/quotes/${id}`)).json();
        setCustomerName(q.customer_name);
        setCurrency(q.currency);
        setDocType(q.doc_type || 'contract');
        setRepairReplace(q.repair_replace === true);
        setValidUntil(q.valid_until ? q.valid_until.slice(0, 10) : '');
        setNotes(q.notes || '');
        setTextBlocks({ ...(q.doc_type === 'acknowledgement' ? ACK_TEXT_BLOCKS : DEFAULT_TEXT_BLOCKS), ...(q.text_blocks || {}) });
      } else if (groupId) {
        await loadGroup(groupId);
      } else {
        const custId = searchParams.get('customer_id');
        if (custId) {
          const [customerRes, groupsRes] = await Promise.all([
            authFetch(`/api/v1/customers/${custId}`),
            authFetch(`/api/v1/opportunity-groups?customer_id=${custId}`)
          ]);
          if (customerRes.ok) setCustomerName((await customerRes.json()).business_name || '');
          setGroupChoices(groupsRes.ok ? ((await groupsRes.json()).data || []) : []);
        } else {
          setGroupChoices([]);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load the builder');
    } finally {
      setLoading(false);
    }
  }, [authFetch, id, isEdit, groupId, searchParams, loadGroup]);

  useEffect(() => { load(); }, [load]);

  const includedItems = (group?.line_items || []).filter(item => included[item.opportunity_id]);
  const subtotalMinor = includedItems.reduce((sum, item) => sum + Number(item.value_minor_units || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      setSaving(true);
      if (isEdit) {
        const res = await authFetch(`/api/v1/quotes/${id}`, {
          method: 'PUT',
          body: JSON.stringify({
            doc_type: docType,
            ...(isAck ? { repair_replace: repairReplace } : {}),
            valid_until: validUntil || null,
            notes: notes.trim() || null,
            text_blocks: textBlocks
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to save');
        navigate(`/quotes/${id}`);
        return;
      }

      if (includedItems.length === 0) {
        throw new Error('Include at least one line item.');
      }
      // R1 (DL-7): prices are SERVER-DERIVED from each linked line item —
      // the client sends references, never amounts.
      const items = includedItems.map(item => (
        item.line_type === 'product'
          ? {
            line_type: 'product',
            opportunity_id: item.opportunity_id,
            product_type: item.product,
            title: (item.product || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            quantity: 1
          }
          : {
            line_type: 'non_asset',
            opportunity_id: item.opportunity_id,
            title: item.description,
            quantity: 1
          }
      ));
      const res = await authFetch('/api/v1/quotes', {
        method: 'POST',
        body: JSON.stringify({
          opportunity_group_id: group.group_id,
          doc_type: docType,
          ...(isAck ? { repair_replace: repairReplace } : {}),
          currency,
          valid_until: validUntil || null,
          notes: notes.trim() || null,
          text_blocks: textBlocks,
          items
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create the document');
      navigate(`/quotes/${data.quote_id}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (loading) return <div className="quote-detail-page"><p>Loading…</p></div>;

  // Customer entry point without a group: pick one.
  if (!isEdit && !group) {
    return (
      <div className="quote-detail-page quote-builder">
        <div className="page-header">
          <div>
            <h1>New {DOC_TYPE_NAMES[docType]}</h1>
            <p>{customerName || 'Pick the opportunity to generate from'}</p>
          </div>
          <BackToList to="/pipeline" className="btn-secondary">Cancel</BackToList>
        </div>
        {error && <div className="modal-error">{error}</div>}
        <section className="builder-section">
          <h2>Which opportunity?</h2>
          {(groupChoices || []).length === 0 ? (
            <p className="line-sub">
              No active opportunities found. <Link to="/pipeline/new">Create an opportunity</Link> first — documents are generated from its line items.
            </p>
          ) : (
            groupChoices.map(choice => (
              <div key={choice.group_id} className="opp-row">
                <button type="button" className="btn-secondary" onClick={() => { setGroupId(choice.group_id); }}>
                  {choice.group_ref} — {choice.title} ({choice.item_count} item{choice.item_count === 1 ? '' : 's'})
                </button>
              </div>
            ))
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="quote-detail-page quote-builder">
      <div className="page-header">
        <div>
          <h1>{isEdit ? `Edit ${DOC_TYPE_NAMES[docType]}` : `New ${DOC_TYPE_NAMES[docType]}`}</h1>
          <p>
            {customerName}
            {group && <> · <Link to={`/pipeline/groups/${group.group_id}`}>{group.group_ref} — {group.title}</Link></>}
          </p>
        </div>
        {group ? (
          <Link to={`/pipeline/groups/${group.group_id}`} className="btn-secondary">Cancel</Link>
        ) : (
          <BackToList to="/pipeline" className="btn-secondary">Cancel</BackToList>
        )}
      </div>

      {error && <div className="modal-error">{error}</div>}

      <form onSubmit={submit}>
        {!isEdit && group && (
          <section className="builder-section">
            <h2>Line items</h2>
            <p className="line-sub">Only approved (or no-approval) line items can be included — excluded items stay reviewable on the Opportunity.</p>
            {(group.line_items || []).map(item => {
              const { eligible, label } = lineEligibility(item);
              return (
                <div key={item.opportunity_id} className="opp-row">
                  <label className="checkbox-label" style={!eligible ? { opacity: 0.55 } : undefined}>
                    <input
                      type="checkbox"
                      checked={!!included[item.opportunity_id]}
                      disabled={!eligible}
                      onChange={(e) => setIncluded(prev => ({ ...prev, [item.opportunity_id]: e.target.checked }))}
                    />
                    <span>
                      {item.opp_ref} — {item.line_type === 'product' ? (item.product || '').replace(/_/g, ' ') : `${item.description} (non-product)`}
                      {' · '}{formatMoney(Number(item.value_minor_units || 0), item.currency)}
                      {label && <em className="placeholder-badge"> {label}</em>}
                    </span>
                  </label>
                </div>
              );
            })}
          </section>
        )}

        <section className="builder-section">
          <h2>Document</h2>
          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select
                value={docType}
                disabled={isEdit && isAck}
                onChange={(e) => {
                  const next = e.target.value;
                  // The wording follows the type, unless it is being edited.
                  if (!isEdit && (next === 'acknowledgement') !== isAck) {
                    setTextBlocks({ ...(next === 'acknowledgement' ? ACK_TEXT_BLOCKS : DEFAULT_TEXT_BLOCKS) });
                  }
                  setDocType(next);
                }}
              >
                {!ackOnly && <option value="contract">Contract (default)</option>}
                {!ackOnly && <option value="quote">Quote</option>}
                {(isAck || (group?.is_order && group?.order_accepted_at)) && (
                  <option value="acknowledgement">Order acknowledgement</option>
                )}
              </select>
            </div>
            {!isAck && (
              <div className="form-group">
                <label>Valid until</label>
                <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
              </div>
            )}
            <div className="form-group">
              <label>Currency</label>
              <input value={currency} disabled title="Currency follows the customer" />
            </div>
          </div>
          {isAck && (
            <label className="checkbox-label ack-repair-replace">
              <input type="checkbox" checked={repairReplace} onChange={(e) => setRepairReplace(e.target.checked)} />
              Covered by the repair &amp; replace policy (prices shown, balance due 0)
            </label>
          )}
          <div className="form-group">
            <label>Notes (internal)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </section>

        <section className="builder-section">
          <h2>Wording (editable — prices are not)</h2>
          {Object.keys(isAck ? ACK_TEXT_BLOCKS : TEXT_BLOCK_LABELS).map(key => (
            <div key={key} className="form-group">
              <label>{TEXT_BLOCK_LABELS[key]}</label>
              <textarea
                rows={2}
                value={textBlocks[key] || ''}
                placeholder={key === 'site_survey_requirements' ? 'Free text per document — appears under "Site Survey Requirements"' : ''}
                onChange={(e) => setTextBlocks(prev => ({ ...prev, [key]: e.target.value }))}
              />
            </div>
          ))}
          <p className="line-sub">Use {'{farm}'} to insert the customer name. Prices render from each line item's approved pricing schedule.</p>
        </section>

        <div className="builder-footer">
          {!isEdit && (
            <div className="builder-subtotal">
              Total (one-off): <strong>{formatMoney(subtotalMinor, currency)}</strong>
              {isAck && <> · Balance due: <strong>{formatMoney(repairReplace ? 0 : subtotalMinor, currency)}</strong></>}
              <span className="excludes-tax-inline"> (excludes tax, tariffs &amp; shipping)</span>
            </div>
          )}
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : `Create ${DOC_TYPE_NAMES[docType]}`)}
          </button>
        </div>
      </form>
    </div>
  );
}

export default QuoteBuilder;
